import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Applies the signed-in user's Accent Color (their user-level business
 * profile's `accentColor`) to the app-wide `brand-sage` palette at runtime.
 * Every `bg-brand-sage` / `text-brand-sage` / `border-brand-sage` /
 * `ring-brand-sage` utility resolves to `var(--brand-sage)` (see
 * client/src/index.css), so overriding that variable (plus the derived
 * deep/light shades) on documentElement re-skins the whole authenticated app.
 *
 * When no valid accent exists, the inline overrides are removed and the sage
 * fallbacks from `:root` take over — so the public/marketing pages, which never
 * mount this, always stay sage.
 */

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function toHex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map((v) => clampByte(v).toString(16).padStart(2, "0")).join("");
}

function parseHex(hex: string): [number, number, number] | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

// Blend an rgb triple toward a target channel value (0 = black, 255 = white).
function mixToward([r, g, b]: [number, number, number], target: number, amount: number): string {
  return toHex(r + (target - r) * amount, g + (target - g) * amount, b + (target - b) * amount);
}

export function AccentColorSync() {
  // Shares the Settings page query key so saving a new accent there
  // re-skins the app automatically (that view invalidates this key).
  const { data } = useQuery({
    queryKey: ["business-profile"],
    queryFn: () => api.businessProfile.get(),
    staleTime: 30_000,
    retry: false,
  });

  // The user picks (in onboarding / settings) whether the light or dark accent
  // drives the dashboard via userSettings.dashboardAccentMode.
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.settings.get(),
    staleTime: 30_000,
    retry: false,
  });

  useEffect(() => {
    const root = document.documentElement;
    const clear = () => {
      root.style.removeProperty("--brand-sage");
      root.style.removeProperty("--brand-sage-deep");
      root.style.removeProperty("--brand-sage-light");
    };

    // Choose the active accent: dashboardAccentMode selects light vs dark, with
    // graceful fallbacks to the other accent and the legacy single accentColor.
    const mode = settings?.dashboardAccentMode === "dark" ? "dark" : "light";
    const chosen =
      mode === "dark"
        ? data?.accentColorDark || data?.accentColorLight || data?.accentColor
        : data?.accentColorLight || data?.accentColorDark || data?.accentColor;
    const rgb = chosen ? parseHex(chosen) : null;
    if (!rgb) {
      clear();
      return clear;
    }

    root.style.setProperty("--brand-sage", toHex(rgb[0], rgb[1], rgb[2]));
    root.style.setProperty("--brand-sage-deep", mixToward(rgb, 0, 0.2));
    root.style.setProperty("--brand-sage-light", mixToward(rgb, 255, 0.25));

    // Remove the overrides on unmount (e.g. sign-out / navigating to the
    // public marketing pages) so the :root sage fallbacks take back over.
    return clear;
  }, [data, settings?.dashboardAccentMode]);

  return null;
}
